/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import React, { useMemo } from "react";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { logger } from "@/lib/utils/logger";
import { Button } from "@/components/ui/button";
import { useDesignTokens } from "@/hooks/ui";

export default function Error({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const { getSemanticSurfaceColors } = useDesignTokens();

  const errorSurface = useMemo(
    () => getSemanticSurfaceColors("error"),
    [getSemanticSurfaceColors],
  );
  const neutralSurface = useMemo(
    () => getSemanticSurfaceColors("neutral"),
    [getSemanticSurfaceColors],
  );

  const router = useNavigationWithProgress();

  React.useEffect(() => {
    logger.error("Global error boundary caught error", {
      message: error.message,
      name: error.name,
      stack: error.stack,
    });
  }, [error]);

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{ backgroundColor: neutralSurface.backgroundMuted }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 text-center shadow-lg">
        <div
          className="mb-4 text-2xl"
          style={{ color: errorSurface.text }}
          aria-hidden
        >
          ⚠️
        </div>
        <h1
          className="mb-2 text-base font-semibold"
          style={{ color: neutralSurface.text }}
        >
          Something went wrong
        </h1>
        <p className="mb-4" style={{ color: neutralSurface.textMuted }}>
          An unexpected error occurred. You can retry or return to the
          dashboard.
        </p>
        {process.env.NODE_ENV === "development" && (
          <details className="mb-4 text-left text-xs" open>
            <summary className="cursor-pointer text-muted-foreground">
              Error Details (dev only)
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted p-2 text-xs">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
        <div className="flex items-center justify-center gap-2">
          <Button onClick={() => reset()} variant="default" size="sm">
            Try Again
          </Button>
          <Button
            onClick={() => router.push("/app/dashboard")}
            variant="outline"
            size="sm"
          >
            Go Home
          </Button>
        </div>
      </div>
    </div>
  );
}
